import React, { useState, useEffect } from "react";
import CouponListing from "./CouponListing";
import { columnTypes } from "src/components/UI/atoms/ATMTable/ATMTable";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/redux/store";
import {
  setItems,
  setPage,
  setRowsPerPage,
  setSearchValue,
  setIsTableLoading,
  setTotalItems,
} from "src/redux/slices/CouponSlice";
import { RxPencil1 } from "react-icons/rx";
import { showConfirmationDialog } from "src/utils/showConfirmationDialog";
import ATMMenu from "src/components/UI/atoms/ATMMenu/ATMMenu";
import { showToast } from "src/utils/showToaster";
import AddCouponFormWrapper from "../Add/AddCouponFormWrapper";
import {
  useGetAllCouponWithPaginationQuery,
  useActiveDeactiveCouponMutation,
} from "src/services/CouponServices";
import { formatedDateTimeIntoIst } from "src/utils/formUtils/dateTimeFormate";
import EditCouponFormWrapper from "../Edit/EditCouponFormWrapper";
import { Switch } from "@mui/material";
import { UserModuleActionTypes, UserModuleNameTypes } from "src/models/userAccess/UserAccess.model";
import { isCheckColumnPermission } from "src/helpers/userAccessFun";

type Props = {};

const CouponListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.coupon);

  const [isOpenAddFrom, setIsOpenAddForm] = useState(false);
  const [isOpenEditFrom, setIsOpenEditForm] = useState(false);
  const [couponId, setCouponId] = useState("");
  const [activeDeactiveCoupon] = useActiveDeactiveCouponMutation();
  const { isLoading, isFetching, data } = useGetAllCouponWithPaginationQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["couponCode"],
    page: page,
    filterBy: [],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (isLoading || isFetching) {
      dispatch(setIsTableLoading(true));
    } else {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setIsTableLoading(false));
    }
  }, [isLoading, isFetching, data, dispatch]);

  //Handle switch Active/Deactive
  const handleSwitchActiveDeactiveBookProduct = (
    rowStatus: boolean,
    rowId: string
  ) => {
    showConfirmationDialog({
      title: "Heads Up",
      text: `Are you sure want to ${
        rowStatus ? "Deactive" : "Active"
      } this Coupon ?`,
      icon: "question",
      showCancelButton: true,
      next: (result) => {
        if (result.isConfirmed) {
          activeDeactiveCoupon(rowId).then((res: any) => {
            if (res.error) {
              showToast("error", res?.error?.data?.message);
            } else {
              showToast("success", res?.data?.message);
            }
          });
        }
      },
    });
  };
  const columns: columnTypes[] = [
    {
      field: "couponCode",
      headerName: "Coupon Code",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span> {row?.couponCode} </span>,
    },
    {
      field: "couponType",
      headerName: "Coupon Type",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span> {row?.couponType} </span>,
    },
    {
      field: "discountAmount",
      headerName: "Discount",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <span>
          {" "}
          {row?.amountType !== "PERCENTAGE" ? (
            <span> &#8377;{row?.discountAmount}</span>
          ) : (
            <span> {row?.discountAmount}&#37; </span>
          )}{" "}
        </span>
      ),
    },
    {
      field: "maxAppliedCount",
      headerName: "Max Apply Count",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => <span> {row?.maxAppliedCount} </span>,
    },
    {
      field: "usedCount",
      headerName: "Applied Count",
      flex: "flex-[1_1_0%]",
      extraClasses: "",
    },
    {
      field: "validity",
      headerName: "Validity",
      extraClasses: "py-1",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => (
        <div>
          <div className="text-slate-700 font-medium">
            {formatedDateTimeIntoIst(row.startDate, "DD MMM yyyy")} to
          </div>
          <div className="text-slate-700 font-medium">
            {formatedDateTimeIntoIst(row.endDate || "-", "DD MMM yyyy")}
          </div>
        </div>
      ),
    },
    {
      field: "active_deactive",
      headerName: "Active/Deactive",
      flex: "flex-[1.25_1.25_0%]",
      extraClasses: "min-w-100px]",
      renderCell: (row: any) => {
        return (
          // active deactive

          <Switch
            checked={row?.isActive}
            onChange={(e) => {
              handleSwitchActiveDeactiveBookProduct(row?.isActive, row?._id);
            }}
          />
        );
      },
    },
    {
      noAuthRequired: true,
      field: "action",
      headerName: "Action",
      flex: "flex-[1_1_0%]",
      renderCell: (row: any) => {
        const options = getActionOptions(row);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  const getActionOptions = (row: any) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setIsOpenEditForm(true);
          setCouponId(row?._id);
        },
        moduleName: UserModuleNameTypes.COUPONS,
        actionName: UserModuleActionTypes.EDIT,
      },
    ];
  };
  return (
    <div>
      <CouponListing
        columns={isCheckColumnPermission({
          moduleName: UserModuleNameTypes.COUPONS,
          actionName: UserModuleActionTypes.LIST,
          columns: columns
        })}
        rows={items}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) =>
            dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
        onAddNew={() => setIsOpenAddForm(true)}
      />
      {isOpenAddFrom ? (
        <AddCouponFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditFrom ? (
        <EditCouponFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          couponId={couponId}
        />
      ) : null}
    </div>
  );
};

export default CouponListingWrapper;
